import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import {
  Sun, Check, ArrowRight, Home, BatteryCharging, Zap, ShieldCheck,
  Calendar, Leaf, Sparkles, Wallet, TrendingUp, Grid3x3, CreditCard,
} from "lucide-react";
import { toast } from "sonner";
import LockedNotice from "@/components/LockedNotice";
import InstallmentChooser from "@/components/InstallmentChooser";

// Pictogram + accent palette per tier.
// Keeps a single source of truth, addressable by backend `icon` / `accent` fields.
const ICON_MAP = { sun: Sun, home: Home, battery: BatteryCharging, zap: Zap };

const ACCENTS = {
  amber: {
    chip: "bg-amber-50 text-amber-700 border-amber-200",
    halo: "from-amber-100 to-amber-50",
    iconBg: "bg-gradient-to-br from-amber-100 to-amber-200 text-amber-700",
    ring: "ring-amber-200/60",
    bar: "bg-amber-400",
  },
  gold: {
    chip: "bg-[#FFF7E0] text-[#8A6B14] border-[#E8CF7C]",
    halo: "from-[#FFF1C9] to-[#FFFBF0]",
    iconBg: "bg-gradient-to-br from-[#F3E0A2] to-[#E0B847] text-[#3D2C00]",
    ring: "ring-[#C9A356]/40",
    bar: "bg-[#C9A356]",
  },
  navy: {
    chip: "bg-[#E8EEF8] text-[#0A2540] border-[#C7D4E8]",
    halo: "from-slate-200 to-slate-50",
    iconBg: "bg-gradient-to-br from-[#1E3A66] to-[#0A2540] text-white",
    ring: "ring-slate-300",
    bar: "bg-[#0A2540]",
  },
};

function SpecPip({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-center gap-2 rounded-md bg-slate-50 px-2.5 py-2 border border-slate-100" data-testid={testId}>
      <Icon className="w-4 h-4 text-[#0A2540] shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 leading-none">{label}</div>
        <div className="text-sm font-medium text-[#0A2540] leading-tight truncate">{value}</div>
      </div>
    </div>
  );
}

export default function SolarPackages() {
  const { t } = useTranslation();
  const [packages, setPackages] = useState([]);
  const [submitting, setSubmitting] = useState(null);
  const [locked, setLocked] = useState(false);
  const [installPkg, setInstallPkg] = useState(null);

  useEffect(() => {
    api.get("/packages/solar")
      .then((r) => setPackages(r.data))
      .catch((e) => { if (e?.response?.status === 403) setLocked(true); });
  }, []);

  const requestQuote = async (pkg) => {
    setSubmitting(pkg.package_id);
    try {
      await api.post("/requests", {
        request_type: "quote",
        selected_package_id: pkg.package_id,
        notes: `Requesting quote for ${pkg.name}`,
      });
      toast.success("Quote request submitted! Our team will be in touch.");
    } catch (err) {
      console.error("Quote submit failed:", err);
      toast.error("Failed to submit");
    } finally {
      setSubmitting(null);
    }
  };

  if (locked) {
    return <LockedNotice
      title="Packages unlock after consultation"
      body="To protect our pricing, full package details are shared only with approved clients. Book your free consultation — once we've spoken, your account is upgraded automatically."
      ctaLabel="Schedule consultation"
      ctaTo="/dashboard/services"
    />;
  }

  return (
    <div data-testid="solar-packages-page">
      <p className="overline">Find your fit</p>
      <h1 className="text-3xl font-heading font-medium text-[#0A2540] mt-2">{t("packages.title")}</h1>
      <p className="text-[#475569] mb-8 max-w-2xl">{t("packages.intro")}</p>

      <div className="grid md:grid-cols-3 gap-6">
        {packages.map((p) => {
          const Icon = ICON_MAP[p.icon] || Sun;
          const a = ACCENTS[p.accent] || ACCENTS.gold;
          const isSubmitting = submitting === p.package_id;
          return (
            <div
              key={p.package_id}
              data-testid={`package-${p.package_id}`}
              className={`group relative card-pb overflow-hidden flex flex-col transition-all hover:-translate-y-1 hover:shadow-xl ${p.popular ? `ring-2 ${a.ring}` : ""}`}
            >
              {/* Accent halo */}
              <div className={`absolute inset-x-0 top-0 h-32 bg-gradient-to-b ${a.halo} opacity-70 pointer-events-none`} />
              {p.popular && (
                <div className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#C9A356] text-white text-[11px] font-semibold tracking-wide shadow-sm">
                  <Sparkles className="w-3 h-3" /> {t("packages.mostPopular")}
                </div>
              )}

              <div className="relative p-7 pb-5">
                {/* Hero pictogram */}
                <div className={`w-20 h-20 rounded-2xl ${a.iconBg} grid place-items-center shadow-md mb-5 transition-transform group-hover:scale-105`} data-testid={`package-${p.package_id}-icon`}>
                  <Icon className="w-10 h-10" strokeWidth={1.5} />
                </div>

                <div className={`inline-block px-2 py-0.5 rounded-md border text-[11px] font-medium uppercase tracking-wider ${a.chip} mb-3`}>{p.tagline}</div>
                <h3 className="font-heading text-2xl font-medium text-[#0A2540]">{p.name}</h3>
                <p className="text-sm text-[#475569] mt-1 min-h-[40px]">{p.best_for}</p>

                {/* Price block */}
                <div className="mt-5">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[11px] uppercase tracking-wider text-[#475569]">{t("common.from")}</span>
                    <span className="text-4xl font-heading font-bold text-[#0A2540]" data-testid={`package-${p.package_id}-price`}>${(p.price || 0).toLocaleString()}</span>
                  </div>
                  {p.monthly_from ? (
                    <div className="text-sm text-[#475569] mt-1">or ${p.monthly_from}/mo with financing</div>
                  ) : null}
                </div>
              </div>

              {/* Spec grid */}
              <div className="px-7 grid grid-cols-2 gap-2">
                <SpecPip icon={Grid3x3} label={t("packages.panels")} value={`${p.panels || "—"}`} testId={`package-${p.package_id}-panels`} />
                <SpecPip icon={Zap} label={t("packages.system")} value={p.system_size_kw ? `${p.system_size_kw} kW` : "—"} testId={`package-${p.package_id}-kw`} />
                <SpecPip icon={Wallet} label={t("packages.billSaved")} value={p.monthly_savings ? `~$${p.monthly_savings}` : "—"} testId={`package-${p.package_id}-savings`} />
                <SpecPip icon={Leaf} label={t("packages.co2Saved")} value={p.co2_offset_tons ? `${p.co2_offset_tons}t/yr` : "—"} testId={`package-${p.package_id}-co2`} />
                <SpecPip icon={Calendar} label={t("packages.install")} value={`${p.install_days || 4} ${t("packages.days")}`} />
                <SpecPip icon={ShieldCheck} label={t("packages.warranty")} value={`${p.warranty_years || 15} ${t("packages.years")}`} />
              </div>

              {/* What's included */}
              <div className="px-7 pt-5">
                <div className="text-[11px] uppercase tracking-wider text-[#475569] font-medium mb-2">{t("packages.whatsIncluded")}</div>
                <ul className="space-y-1.5 mb-5">
                  {(p.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[#334155]">
                      <Check className="w-4 h-4 text-[#C9A356] mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-auto p-7 pt-2">
                <button
                  onClick={() => requestQuote(p)}
                  disabled={isSubmitting}
                  className={`btn-primary w-full ${p.popular ? "shadow-md" : ""}`}
                  data-testid={`request-quote-${p.package_id}`}
                >
                  {isSubmitting ? "Submitting..." : <>{t("packages.requestQuote")} <ArrowRight className="w-4 h-4 ml-2" /></>}
                </button>
                <button
                  onClick={() => setInstallPkg(p)}
                  data-testid={`pay-monthly-${p.package_id}`}
                  className="w-full mt-2 text-xs font-semibold text-[#C9A356] hover:underline inline-flex items-center justify-center gap-1.5"
                >
                  {p.monthly_from ? (
                    <><CreditCard className="w-3.5 h-3.5" /> Or pay ${p.monthly_from}/mo on a card plan</>
                  ) : null}
                </button>
                <p className="text-[11px] text-center text-[#94A3B8] mt-2 flex items-center justify-center gap-1">
                  <TrendingUp className="w-3 h-3" /> {t("packages.freeSiteVisit")}
                </p>
              </div>

              <div className={`h-1 w-full ${a.bar}`} />
            </div>
          );
        })}
      </div>
      {installPkg && <InstallmentChooser pkg={installPkg} onClose={() => setInstallPkg(null)} />}
    </div>
  );
}
